import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { Toaster } from "sonner";
import Layout from "@/components/Layout";
import AuthCallback from "@/pages/AuthCallback";
import { Suspense, lazy, useEffect } from "react";

const HomePage = lazy(() => import("@/pages/HomePage"));
const LoginPage = lazy(() => import("@/pages/LoginPage"));
const RegisterPage = lazy(() => import("@/pages/RegisterPage"));
const SearchPage = lazy(() => import("@/pages/SearchPage"));
const ListingDetailPage = lazy(() => import("@/pages/ListingDetailPage"));
const CreateListingPage = lazy(() => import("@/pages/CreateListingPage"));
const DashboardPage = lazy(() => import("@/pages/DashboardPage"));
const InboxPage = lazy(() => import("@/pages/InboxPage"));
const DealerDirectoryPage = lazy(() => import("@/pages/DealerDirectoryPage"));
const AdminPage = lazy(() => import("@/pages/AdminPage"));
const DealerProfilePage = lazy(() => import("@/pages/DealerProfilePage"));
const TermsPage = lazy(() => import("@/pages/TermsPage"));
const DealerOnboardingPage = lazy(() => import("@/pages/DealerOnboardingPage"));
const VerifyEmailPage = lazy(() => import("@/pages/VerifyEmailPage"));
const PrivacyPolicyPage = lazy(() => import("@/pages/PrivacyPolicyPage"));
const NewsPage = lazy(() => import("@/pages/NewsPage"));
const NewsPostPage = lazy(() => import("@/pages/NewsPostPage"));
const ClaimAccountPage = lazy(() => import("@/pages/ClaimAccountPage"));
const AdvertisePage = lazy(() => import("@/pages/AdvertisePage"));
const ContactPage = lazy(() => import("@/pages/ContactPage"));
const ShootingClubsPage = lazy(() => import("@/pages/ShootingClubsPage"));
const ClubProfilePage = lazy(() => import("@/pages/ClubProfilePage"));
const PriceCheckerPage = lazy(() => import("@/pages/PriceCheckerPage"));
const ClaimClubPage = lazy(() => import("@/pages/ClaimClubPage"));
const ComplaintsPolicyPage = lazy(() => import("@/pages/ComplaintsPolicyPage"));
const NotFoundPage = lazy(() => import("@/pages/NotFoundPage"));
const ForgotPasswordPage = lazy(() => import("@/pages/ForgotPasswordPage"));
const CompleteProfilePage = lazy(() => import("@/pages/CompleteProfilePage"));
const GunsIndexPage = lazy(() => import("@/pages/guns/GunsIndexPage"));
const GunBrandPage = lazy(() => import("@/pages/guns/GunBrandPage"));
const GunModelPage = lazy(() => import("@/pages/guns/GunModelPage"));
const PricesIndexPage = lazy(() => import("@/pages/prices/PricesIndexPage"));
const PriceComparePage = lazy(() => import("@/pages/prices/PriceComparePage"));
const EventsPage = lazy(() => import("@/pages/EventsPage"));
const EventDetailPage = lazy(() => import("@/pages/EventDetailPage"));

function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-[50vh]">
      <div className="w-8 h-8 border-[3px] border-slate-200 border-t-slate-700 rounded-full animate-spin" />
    </div>
  );
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="animate-spin h-8 w-8 border-4 border-slate-900 border-t-transparent rounded-full"></div>
    </div>
  );
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

// Redirect component for SEO files that need to be served by the backend
function SeoFileRedirect({ file }) {
  useEffect(() => {
    window.location.replace(`/api/${file}`);
  }, [file]);
  return null;
}

function AppRouter() {
  const location = useLocation();
  // CRITICAL: Check URL fragment for session_id synchronously before any routing
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/sitemap.xml" element={<SeoFileRedirect file="sitemap.xml" />} />
      <Route path="/robots.txt" element={<SeoFileRedirect file="robots.txt" />} />
      <Route path="/llms.txt" element={<SeoFileRedirect file="llms.txt" />} />
      <Route element={<Layout />}>
        <Route path="/" element={<Suspense fallback={<PageLoader />}><HomePage /></Suspense>} />
        <Route path="/search" element={<Suspense fallback={<PageLoader />}><SearchPage /></Suspense>} />
        <Route path="/rifles" element={<Suspense fallback={<PageLoader />}><SearchPage defaultCategory="rifle" /></Suspense>} />
        <Route path="/rifles/:state" element={<Suspense fallback={<PageLoader />}><SearchPage defaultCategory="rifle" /></Suspense>} />
        <Route path="/shotguns" element={<Suspense fallback={<PageLoader />}><SearchPage defaultCategory="shotgun" /></Suspense>} />
        <Route path="/shotguns/:state" element={<Suspense fallback={<PageLoader />}><SearchPage defaultCategory="shotgun" /></Suspense>} />
        <Route path="/handguns" element={<Suspense fallback={<PageLoader />}><SearchPage defaultCategory="handgun" /></Suspense>} />
        <Route path="/handguns/:state" element={<Suspense fallback={<PageLoader />}><SearchPage defaultCategory="handgun" /></Suspense>} />
        <Route path="/air-rifles" element={<Suspense fallback={<PageLoader />}><SearchPage defaultCategory="air_rifle" /></Suspense>} />
        <Route path="/air-rifles/:state" element={<Suspense fallback={<PageLoader />}><SearchPage defaultCategory="air_rifle" /></Suspense>} />
        <Route path="/muzzleloaders" element={<Suspense fallback={<PageLoader />}><SearchPage defaultCategory="muzzleloader" /></Suspense>} />
        <Route path="/muzzleloaders/:state" element={<Suspense fallback={<PageLoader />}><SearchPage defaultCategory="muzzleloader" /></Suspense>} />
        <Route path="/scopes-optics" element={<Suspense fallback={<PageLoader />}><SearchPage defaultCategory="scopes_optics" /></Suspense>} />
        <Route path="/scopes-optics/:state" element={<Suspense fallback={<PageLoader />}><SearchPage defaultCategory="scopes_optics" /></Suspense>} />
        <Route path="/ammo" element={<Suspense fallback={<PageLoader />}><SearchPage defaultCategory="ammo" /></Suspense>} />
        <Route path="/ammo/:state" element={<Suspense fallback={<PageLoader />}><SearchPage defaultCategory="ammo" /></Suspense>} />
        <Route path="/paintball-archery" element={<Suspense fallback={<PageLoader />}><SearchPage defaultCategory="paintball_archery" /></Suspense>} />
        <Route path="/paintball-archery/:state" element={<Suspense fallback={<PageLoader />}><SearchPage defaultCategory="paintball_archery" /></Suspense>} />
        <Route path="/parts-accessories" element={<Suspense fallback={<PageLoader />}><SearchPage defaultCategory="parts_accessories" /></Suspense>} />
        <Route path="/parts-accessories/:state" element={<Suspense fallback={<PageLoader />}><SearchPage defaultCategory="parts_accessories" /></Suspense>} />
        <Route path="/antique-replicas" element={<Suspense fallback={<PageLoader />}><SearchPage defaultCategory="antique_replicas" /></Suspense>} />
        <Route path="/antique-replicas/:state" element={<Suspense fallback={<PageLoader />}><SearchPage defaultCategory="antique_replicas" /></Suspense>} />
        <Route path="/firearm-storage" element={<Suspense fallback={<PageLoader />}><SearchPage defaultCategory="firearm_storage" /></Suspense>} />
        <Route path="/firearm-storage/:state" element={<Suspense fallback={<PageLoader />}><SearchPage defaultCategory="firearm_storage" /></Suspense>} />
        <Route path="/hunting-services" element={<Suspense fallback={<PageLoader />}><SearchPage defaultCategory="hunting_services" /></Suspense>} />
        <Route path="/hunting-services/:state" element={<Suspense fallback={<PageLoader />}><SearchPage defaultCategory="hunting_services" /></Suspense>} />
        <Route path="/listing/:slug" element={<Suspense fallback={<PageLoader />}><ListingDetailPage /></Suspense>} />
        <Route path="/guns" element={<Suspense fallback={<PageLoader />}><GunsIndexPage /></Suspense>} />
        <Route path="/guns/:brandId" element={<Suspense fallback={<PageLoader />}><GunBrandPage /></Suspense>} />
        <Route path="/guns/:brandId/:modelId" element={<Suspense fallback={<PageLoader />}><GunModelPage /></Suspense>} />
        <Route path="/prices" element={<Suspense fallback={<PageLoader />}><PricesIndexPage /></Suspense>} />
        <Route path="/prices/:brandSlug/:slug" element={<Suspense fallback={<PageLoader />}><PriceComparePage /></Suspense>} />
        <Route path="/events" element={<Suspense fallback={<PageLoader />}><EventsPage /></Suspense>} />
        <Route path="/events/past" element={<Suspense fallback={<PageLoader />}><EventsPage past /></Suspense>} />
        <Route path="/events/:slug" element={<Suspense fallback={<PageLoader />}><EventDetailPage /></Suspense>} />
        <Route path="/dealers" element={<Suspense fallback={<PageLoader />}><DealerDirectoryPage /></Suspense>} />
        <Route path="/dealer/:id" element={<Suspense fallback={<PageLoader />}><DealerProfilePage /></Suspense>} />
        <Route path="/shooting-clubs" element={<Suspense fallback={<PageLoader />}><ShootingClubsPage /></Suspense>} />
        <Route path="/club/:id" element={<Suspense fallback={<PageLoader />}><ClubProfilePage /></Suspense>} />
        <Route path="/price-checker" element={<Suspense fallback={<PageLoader />}><PriceCheckerPage /></Suspense>} />
        <Route path="/login" element={<Suspense fallback={<PageLoader />}><LoginPage /></Suspense>} />
        <Route path="/register" element={<Suspense fallback={<PageLoader />}><RegisterPage /></Suspense>} />
        <Route path="/dashboard" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><DashboardPage /></Suspense></ProtectedRoute>} />
        <Route path="/sell" element={<Navigate to="/dashboard/create-listing" replace />} />
        <Route path="/dashboard/create-listing" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><CreateListingPage /></Suspense></ProtectedRoute>} />
        <Route path="/dashboard/edit-listing/:id" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><CreateListingPage /></Suspense></ProtectedRoute>} />
        <Route path="/dashboard/inbox" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><InboxPage /></Suspense></ProtectedRoute>} />
        <Route path="/admin" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><AdminPage /></Suspense></ProtectedRoute>} />
        <Route path="/terms" element={<Suspense fallback={<PageLoader />}><TermsPage /></Suspense>} />
        <Route path="/dealer-onboarding" element={<Suspense fallback={<PageLoader />}><DealerOnboardingPage /></Suspense>} />
        <Route path="/complete-profile" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><CompleteProfilePage /></Suspense></ProtectedRoute>} />
        <Route path="/verify-email" element={<Suspense fallback={<PageLoader />}><VerifyEmailPage /></Suspense>} />
        <Route path="/claim-account" element={<Suspense fallback={<PageLoader />}><ClaimAccountPage /></Suspense>} />
        <Route path="/claim-club" element={<Suspense fallback={<PageLoader />}><ClaimClubPage /></Suspense>} />
        <Route path="/advertise" element={<Suspense fallback={<PageLoader />}><AdvertisePage /></Suspense>} />
        <Route path="/contact" element={<Suspense fallback={<PageLoader />}><ContactPage /></Suspense>} />
        <Route path="/privacy" element={<Suspense fallback={<PageLoader />}><PrivacyPolicyPage /></Suspense>} />
        <Route path="/complaints-policy" element={<Suspense fallback={<PageLoader />}><ComplaintsPolicyPage /></Suspense>} />
        <Route path="/news" element={<Suspense fallback={<PageLoader />}><NewsPage /></Suspense>} />
        <Route path="/news/:slug" element={<Suspense fallback={<PageLoader />}><NewsPostPage /></Suspense>} />
        <Route path="/forgot-password" element={<Suspense fallback={<PageLoader />}><ForgotPasswordPage /></Suspense>} />
        <Route path="/clubs" element={<Navigate to="/shooting-clubs" replace />} />
        <Route path="*" element={<Suspense fallback={<PageLoader />}><NotFoundPage /></Suspense>} />
      </Route>
    </Routes>
  );
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

const NOINDEX_PREFIXES = [
  "/dashboard", "/admin", "/login", "/register", "/sell", "/inbox",
  "/verify-email", "/claim-account", "/claim-club", "/complete-profile",
  "/forgot-password", "/reset-password", "/dealer-onboarding",
];

function SeoSync() {
  const { pathname } = useLocation();
  useEffect(() => {
    // Self-canonicalise every route on SPA navigation (pages with richer
    // logic, e.g. SearchPage pagination, override after their data loads).
    const stripped = pathname.replace(/\/+$/, "") || "/";
    const canonicalUrl = `https://gunbuy.com.au${stripped === "/" ? "" : stripped}`;
    const canon = document.querySelector('link[rel="canonical"]');
    if (canon) canon.setAttribute("href", canonicalUrl);
    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) ogUrl.setAttribute("content", canonicalUrl);

    // noindex private/utility routes
    const isPrivate = NOINDEX_PREFIXES.some((p) => stripped === p || stripped.startsWith(`${p}/`));
    let robots = document.querySelector('meta[name="robots"]');
    if (isPrivate) {
      if (!robots) {
        robots = document.createElement("meta");
        robots.name = "robots";
        document.head.appendChild(robots);
      }
      robots.setAttribute("content", "noindex, nofollow");
    } else if (robots) {
      robots.remove();
    }
  }, [pathname]);
  return null;
}

function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <SeoSync />
      <AuthProvider>
        <AppRouter />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
